/**
 * 数字人页积分：展示 + 提交前校验（扣费在业务接口完成）
 * 全驱动按配音时长（秒）× 单价计费
 */
import {
  DH_MODEL_CREDITS_FALLBACK,
  DH_MODEL_FULL_FAST,
  DH_MODEL_FULL_STANDARD,
  DH_MODEL_REWRITE,
  DH_MODEL_VOICE_CLONE,
  digitalHumanFullDriveModelId
} from '@/constants/digitalHumanModelIds'
import {
  assertUserHasCredits,
  calcRequiredCreditsFromCatalog,
  fetchModelsList,
  findModelInCatalog,
  NGC_MODEL_KIND
} from '@/utils/modelsListApi'

let _catalogPromise = null
let _catalog = null

export async function loadDigitalHumanModelCatalog(force = false) {
  if (!force && Array.isArray(_catalog) && _catalog.length) {
    return _catalog
  }
  if (!force && _catalogPromise) {
    return _catalogPromise
  }
  _catalogPromise = fetchModelsList()
    .then((list) => {
      _catalog = Array.isArray(list) ? list : []
      return _catalog
    })
    .catch(() => {
      _catalog = []
      return _catalog
    })
    .finally(() => {
      _catalogPromise = null
    })
  return _catalogPromise
}

function creditsForModel(catalog, modelId, kind, opts = {}) {
  const id = String(modelId || '').trim()
  if (!id) return 0
  const fromApi = calcRequiredCreditsFromCatalog(catalog, id, kind, opts)
  if (fromApi > 0) return fromApi
  const base = Number(DH_MODEL_CREDITS_FALLBACK[id]) || 0
  if (base <= 0) return 0
  const times = Math.max(1, Number(opts.times) || 1)
  const duration = Math.max(1, Number(opts.duration) || 1)
  const hit = findModelInCatalog(catalog, id, kind)
  const unit = hit ? String(hit.priceUnit || '次') : id.startsWith('image-human-full') ? '秒' : '次'
  if (unit.includes('秒')) return base * duration * times
  return base * times
}

/** 读取音频时长（秒，向上取整，至少 1） */
export function probeAudioDurationSec(url) {
  const src = String(url || '').trim()
  if (!src) return Promise.resolve(0)
  return new Promise((resolve) => {
    let settled = false
    const finish = (sec) => {
      if (settled) return
      settled = true
      try {
        a.removeAttribute('src')
        a.load()
      } catch (e) {
        /* ignore */
      }
      resolve(sec)
    }
    const a = new Audio()
    a.preload = 'metadata'
    const timer = setTimeout(() => finish(0), 12000)
    a.onloadedmetadata = () => {
      clearTimeout(timer)
      const d = Number(a.duration)
      if (!Number.isFinite(d) || d <= 0) {
        finish(0)
        return
      }
      finish(Math.max(1, Math.ceil(d)))
    }
    a.onerror = () => {
      clearTimeout(timer)
      finish(0)
    }
    a.src = src
  })
}

export function rewriteCredits(catalog) {
  return creditsForModel(catalog, DH_MODEL_REWRITE, NGC_MODEL_KIND.LLM)
}

export function voiceCloneCredits(catalog) {
  return creditsForModel(catalog, DH_MODEL_VOICE_CLONE, NGC_MODEL_KIND.AUDIO)
}

/**
 * 全驱动总积分 = 每秒单价 × 配音秒数
 * @param {object[]} catalog
 * @param {'standard'|'fast'} mode
 * @param {number} [durationSec]
 */
export function fullDriveCredits(catalog, mode, durationSec = 0) {
  const id = digitalHumanFullDriveModelId(mode)
  const sec = Math.max(0, Math.ceil(Number(durationSec) || 0))
  if (sec <= 0) {
    // 尚无配音时长时，只展示单价（按 1 秒预估），UI 可另标「/秒」
    return creditsForModel(catalog, id, NGC_MODEL_KIND.VIDEO, { duration: 1 })
  }
  return creditsForModel(catalog, id, NGC_MODEL_KIND.VIDEO, { duration: sec })
}

/** 全驱动每秒单价（后台 credits，单位为秒时） */
export function fullDriveCreditsPerSecond(catalog, mode) {
  const id = digitalHumanFullDriveModelId(mode)
  const hit = findModelInCatalog(catalog, id, NGC_MODEL_KIND.VIDEO)
  if (hit && Number(hit.credits) > 0) return Number(hit.credits)
  return Number(DH_MODEL_CREDITS_FALLBACK[id]) || 0
}

export async function assertDigitalHumanCredits(required, label) {
  const need = Math.max(0, Number(required) || 0)
  if (need <= 0) return
  await assertUserHasCredits({ required: need, label: label || '本次操作' })
}

export {
  DH_MODEL_REWRITE,
  DH_MODEL_VOICE_CLONE,
  DH_MODEL_FULL_STANDARD,
  DH_MODEL_FULL_FAST,
  digitalHumanFullDriveModelId
}
